import { getScriptPath, getScriptBackupPath } from 'owa-config';
import sleep from 'owa-sleep';
import { errorThatWillCauseAlert } from 'owa-trace';
import type { TraceErrorObject } from 'owa-analytics-types';
import { default as rawSchemaUri } from '../__generated__/schema.all.min.graphql';
import getUrlWithAddedQueryParameters from 'owa-url/lib/getUrlWithAddedQueryParameters';
import { type DocumentNode } from 'graphql';

// Capture this value at the time of import, so we can replace it as needed.
// If we just use `getScriptPath()`, we could be in a situation where the path
// is some other value (like the backup path)
const rawSchemaUriBasePath = process.env.IS_WEBPACK ? __webpack_public_path__ : undefined;

type SchemaParseFunction = (s: string) => DocumentNode;
let types: Promise<DocumentNode> | undefined;
const retryDelayMs = 250;
const retryFatalMs = 30_000;

// Only concurrent loads are deduped. Callers own caching the result, so the parsed document can be
// released once it's no longer needed.
export async function loadTypes(parser: SchemaParseFunction): Promise<DocumentNode> {
    if (!types) {
        types = new Promise<DocumentNode>(async (resolve, reject) => {
            try {
                const schema: DocumentNode = await loadSchema(parser);
                resolve(schema);
            } catch (e) {
                reject(e);
            } finally {
                types = undefined;
            }
        });
    }

    return types;
}

async function loadSchema(parser: SchemaParseFunction): Promise<DocumentNode> {
    let rv: DocumentNode | null = null;

    const RETRIES = 3;

    // try a few times against the main CDN
    for (let i = 0; i < RETRIES && rv == null; ++i) {
        rv = await internalLoadSchema(
            getScriptPath(),
            parser,
            i > 0 /* cacheBust */,
            false /* fatal */
        );
    }

    // try a few times against the backup
    for (let i = 0; i < RETRIES && rv == null; ++i) {
        // throw on the final attempt
        rv = await internalLoadSchema(
            getScriptBackupPath(),
            parser,
            i > 0 /* cacheBust */,
            false /* fatal */
        );
    }

    // try once more and throw if we fail
    if (!rv) {
        rv = await internalLoadSchema(
            getScriptBackupPath(),
            parser,
            true /* cacheBust */,
            true /* fatal */
        );
    }

    if (!rv) {
        // unreachable because we're passing fatal to the last call but ts doesn't know that.
        throw new Error('Failed to load schema');
    }

    return rv;
}

async function internalLoadSchema(
    scriptPath: string,
    parser: SchemaParseFunction,
    cacheBust: boolean,
    fatal: boolean
): Promise<DocumentNode | null> {
    let uri = rawSchemaUri?.toString() || '';
    const owaPublicPath = /^OwaPublicPath/;
    if (uri.match(owaPublicPath)) {
        // this would mean something went wrong with the OwaPublicPathPlugin/workerTemplate handshake for setting
        // the path
        uri = uri.replace(owaPublicPath, scriptPath);
        const err: TraceErrorObject = new Error('OwaPublicPath was not set before loading types');
        errorThatWillCauseAlert(err);
    }

    try {
        // The raw schema url is imported as an absolute URL using the configured public path. We should replace that.
        if (rawSchemaUriBasePath) {
            uri = uri.replace(rawSchemaUriBasePath, scriptPath);
        }

        if (cacheBust && uri.startsWith('http')) {
            uri = getUrlWithAddedQueryParameters(uri, { cacheBust: Date.now().toString() }, true);
        }

        const response = await fetch(uri);
        if (!response.ok) {
            throw new Error('Failed to fetch schema');
        }

        var schemaText: string = await response.text();
        return parser(schemaText);
    } catch (e) {
        if (fatal) {
            await sleep(retryFatalMs);
            throw e;
        }

        await sleep(retryDelayMs);
        return null;
    }
}

export function testReset(): void {
    types = undefined;
}
